/* Nexus CRM — Phase 2 Settings (Custom Fields, Members, Roles, Audit, General/Profile)
   Uses existing UI vocabulary: nx-settings-page, nx-table, Btn, Pill, FilterChip, ConfirmModal. */
import { applyBrandingTokens } from '../../shared/branding/index.js';
import { SearchField, Modal, ConfirmDialog, Menu, MenuItem, MenuDivider, EmptyState } from '../../shared/ui/index.jsx';

const { useState: useStateS, useMemo: useMemoS, useEffect: useEffectS } = React;

/* ---------- Lightweight settings store (persisted) ---------- */
const SETTINGS_KEY = 'nx-settings-v1';
const seedSettings = () => ({
  customFields: {
    people:    [{ id: 'cf_p1', label: 'Status', type: 'select', options: ['Active', 'Inactive', 'Pending'], required: false, searchable: true, filterable: true, archived: false }],
    companies: [{ id: 'cf_c1', label: 'Region', type: 'text', required: false, searchable: true, filterable: true, archived: false }],
    deals:     [{ id: 'cf_d1', label: 'Deal Type', type: 'select', options: ['New Business', 'Renewal', 'Expansion', 'Other'], required: true, searchable: false, filterable: true, archived: false },
                { id: 'cf_d2', label: 'Contract Value', type: 'number', required: false, searchable: false, filterable: true, archived: false }],
    tasks:     [{ id: 'cf_t1', label: 'Priority', type: 'select', options: ['Low', 'Medium', 'High', 'Critical'], required: false, searchable: true, filterable: true, archived: false }],
  },
  roles: [
    { id: 'r_admin',  name: 'Admin',         tone: 'purple', members: 2, perms: ['view','create','edit','delete','export','import','settings'] },
    { id: 'r_mgr',    name: 'Manager',       tone: 'blue',   members: 4, perms: ['view','create','edit','export'] },
    { id: 'r_ae',     name: 'Account Executive', tone: 'green', members: 12, perms: ['view','create','edit'] },
    { id: 'r_sdr',    name: 'SDR',           tone: 'amber',  members: 8, perms: ['view','create','edit'] },
    { id: 'r_sales',  name: 'Sales Agent',   tone: 'blue',   members: 6, perms: ['view','create','edit'] },
    { id: 'r_ops',    name: 'Operations',    tone: 'gray',   members: 3, perms: ['view','export','import'] },
    { id: 'r_view',   name: 'Viewer',        tone: 'gray',   members: 5, perms: ['view'] },
  ],
  members: [],
  audit: [],
  whitelabel: { brand: '', domain: '', domainStatus: 'UNVERIFIED', primaryColor: '#125AB0', secondaryColor: '#e8f0fe', accentColor: '#4692EC', successColor: '#0E8C5A', warningColor: '#B5740E', dangerColor: '#DE3217', logo: false, favicon: false, loginBackground: '', loginBanner: '', welcomeMessage: 'Welcome back', loginTagline: 'Powered by URSA', supportEmail: '', supportPhone: '', supportWebsite: '', footerText: '© 2026 URSA', privacyUrl: '', termsUrl: '', cookieUrl: '', applyScope: 'current' },
  supportTickets: [
    { id: 'TKT-101', subject: 'Cannot export deals to CSV', priority: 'High',   status: 'open',     reporter: 'Alex Thompson',  time: '30m ago' },
    { id: 'TKT-100', subject: 'Custom field not appearing in filters', priority: 'Medium', status: 'open', reporter: 'Diego Alvarez', time: '2h ago' },
    { id: 'TKT-099', subject: 'Password reset email not received', priority: 'Medium', status: 'pending', reporter: 'Priya Nair', time: '1d ago' },
    { id: 'TKT-098', subject: 'Dark mode flash on login', priority: 'Low', status: 'resolved', reporter: 'Sophie Martin', time: '2d ago' },
  ],
  profile: { name: '', email: '', phone: '+91 98765 43210', avatarTone: '#125AB0', darkMode: false,
             notifications: { mentions: true, taskDue: true, dealStage: true, weeklyDigest: false, productNews: false } },
});

const LEGACY_BRAND_NAMES = ['Nexus CRM', 'Nexus Platform', 'Nexus', 'NEXUS', 'nexus crm'];
const migrateWl = (wl) => {
  if (!wl) return wl;
  const m = { ...wl };
  if (LEGACY_BRAND_NAMES.some(n => m.brand === n)) m.brand = '';
  if (typeof m.loginTagline === 'string' && m.loginTagline.toLowerCase().includes('nexus')) m.loginTagline = WL_DEFAULTS.loginTagline;
  if (typeof m.footerText === 'string' && m.footerText.toLowerCase().includes('nexus')) m.footerText = WL_DEFAULTS.footerText;
  if (typeof m.supportEmail === 'string' && m.supportEmail.toLowerCase().includes('nexus')) m.supportEmail = '';
  return m;
};
const loadSettings = () => { try { const s = localStorage.getItem(SETTINGS_KEY); if (!s) return seedSettings(); const p = JSON.parse(s); return { ...p, whitelabel: migrateWl(p.whitelabel) }; } catch (_) { return seedSettings(); } };
const saveSettings = (s) => { try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)); } catch (_) {} };

const useSettingsStore = () => {
  const [state, setState] = useStateS(loadSettings);
  const [themeMode, setThemeMode] = useStateS(() => (window.NexusTheme && window.NexusTheme.get()) || 'light');
  useEffectS(() => { saveSettings(state); window.__nx_settings = state; }, [state]);
  useEffectS(() => {
    const user = window.__nx_auth?.user;
    if (!user) return;
    setState(s => ({
      ...s,
      profile: {
        ...s.profile,
        name: s.profile.name || user.name || '',
        email: s.profile.email || user.email || '',
      },
    }));
  }, []);
  useEffectS(() => {
    if (!window.NexusTheme) return;
    return window.NexusTheme.subscribe((mode) => setThemeMode(mode));
  }, []);
  const update = (patch) => setState(s => typeof patch === 'function' ? patch(s) : { ...s, ...patch });
  const addAudit = (entry) => setState(s => ({ ...s, audit: [{ id: 'a_' + Date.now(), time: 'just now', ...entry }, ...s.audit].slice(0, 200) }));
  const setTheme = (mode) => {
    if (window.NexusTheme) window.NexusTheme.set(mode);
    setState(s => ({ ...s, profile: { ...s.profile, theme: mode, darkMode: mode === 'dark' || (mode === 'system' && window.NexusTheme && window.NexusTheme.getResolved() === 'dark') } }));
    addAudit({ who: state.profile.name, action: 'changed', target: 'Appearance: ' + mode.charAt(0).toUpperCase() + mode.slice(1), module: 'Profile' });
  };
  const appearanceLabel = () => themeMode === 'system' ? 'System' : themeMode === 'dark' ? 'Dark' : 'Light';
  const appearanceTone  = () => themeMode === 'dark' ? 'purple' : themeMode === 'system' ? 'gray' : 'blue';
  return { state, update, addAudit, setState, themeMode, setTheme, appearanceLabel, appearanceTone };
};

/* ---------- Helpers ---------- */
const PERM_LABELS = { view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete', export: 'Export', import: 'Import', settings: 'Settings Access' };
const MODULE_LABELS = { people: 'People', companies: 'Companies', deals: 'Deals', tasks: 'Tasks' };

/* =========================================================
   ROOT SettingsView
   ========================================================= */
const ACCT_SECTIONS = ['general', 'appearance', 'support'];

const SettingsView = () => {
  const ss = useSettingsStore();
  const [section, _setSection] = useStateS(() => {
    try {
      const s = new URLSearchParams(window.location.search).get('section');
      return (s && ACCT_SECTIONS.includes(s)) ? s : 'general';
    } catch (_) { return 'general'; }
  });
  const setSection = (id) => {
    _setSection(id);
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('section', id);
      u.searchParams.delete('tab');
      window.history.replaceState(null, '', u.pathname + u.search);
    } catch (_) {}
  };
  useEffectS(() => {
    const onGo = () => setSection('appearance');
    window.addEventListener('nx-go-appearance', onGo);
    return () => window.removeEventListener('nx-go-appearance', onGo);
  }, []);
  const sections = [
    { group: 'Account', items: [
      { id: 'general',    label: 'Profile & Security',  icon: 'User'          },
      { id: 'appearance', label: 'Appearance',          icon: 'Moon'          },
    ]},
    { group: 'Support', items: [
      { id: 'support',    label: 'Support Center',      icon: 'MessageCircle' },
    ]},
  ];
  if (window.isSuperAdmin && window.isSuperAdmin()) {
    sections.push({ group: 'Administration', items: [
      { id: 'control-center', label: 'Control Center', icon: 'Shield' },
    ]});
  }
  /* Full-screen Control Center — hijacks entire view, hides settings sidebar */
  if (section === 'control-center' && window.ControlCenter) {
    return (
      <div className="nx-view nx-cc-fullscreen">
        <window.ControlCenter onExit={() => setSection('general')} />
      </div>
    );
  }
  const settingsNav = (
    <>
      <div className="nx-settings-side-head">
        <span className="nx-settings-title">Account Settings</span>
      </div>
      {sections.map(s => (
        <div key={s.group} className="nx-settings-group">
          <span className="nx-settings-group-label">{s.group}</span>
          {s.items.map(it => (
            <button key={it.id} className={'nx-settings-item' + (section === it.id ? ' is-active' : '')} onClick={() => setSection(it.id)} title={it.label}>
              <NexusIcon name={it.icon} size={14} /><span>{it.label}</span>
            </button>
          ))}
        </div>
      ))}
    </>
  );
  return (
    <SettingsWorkspaceLayout nav={settingsNav}>
      {section === 'general'     && <SettingsGeneral ss={ss} />}
      {section === 'appearance'  && <SettingsAppearance ss={ss} />}
      {section === 'support'     && <SettingsSupportCenter ss={ss} />}
      {section === 'control-center' && window.ControlCenter && <window.ControlCenter />}
    </SettingsWorkspaceLayout>
  );
};

/* =========================================================
   GENERAL & PROFILE — card-based enterprise layout
   (role NOT editable here)
   ========================================================= */
const SettingsGeneral = ({ ss }) => {
  const toast = useToast();
  const { profile } = ss.state;
  const [draft, setDraft] = useStateS(profile);
  const [pwd, setPwd] = useStateS({ current: '', next: '', confirm: '' });
  useEffectS(() => setDraft(profile), [profile]);

  const save = () => {
    const patch = {};
    if (draft.name !== profile.name) patch.name = draft.name;
    if (draft.phone !== profile.phone) patch.phone = draft.phone;
    if (draft.avatarTone !== profile.avatarTone) patch.avatarTone = draft.avatarTone;
    if (draft.timezone !== profile.timezone) patch.timezone = draft.timezone;
    if (draft.locale !== profile.locale) patch.locale = draft.locale;

    ss.update(s => ({ ...s, profile: { ...s.profile, ...draft } }));
    ss.addAudit({ who: profile.name, action: 'updated', target: 'Profile Settings', module: 'Profile' });

    if (Object.keys(patch).length > 0 && window.__nx_api?.auth?.updateProfile) {
      window.__nx_api.auth.updateProfile(patch).catch(() => {});
    }
    toast({ tone: 'success', icon: 'Check', title: 'Profile Saved' });
  };
  const changePwd = () => {
    if (!pwd.current || !pwd.next) return toast({ tone: 'warning', icon: 'AlertCircle', title: 'Fill All Password Fields' });
    if (pwd.next !== pwd.confirm) return toast({ tone: 'error', icon: 'AlertCircle', title: "Passwords Don't Match" });
    if (pwd.next.length < 8) return toast({ tone: 'warning', icon: 'AlertCircle', title: 'Use At Least 8 Characters' });
    window.__nx_api.auth.changePassword(pwd.current, pwd.next)
      .then(() => {
        ss.addAudit({ who: profile.name, action: 'updated', target: 'Password', module: 'Profile' });
        toast({ tone: 'success', icon: 'Check', title: 'Password Updated' });
        setPwd({ current: '', next: '', confirm: '' });
      })
      .catch(err => {
        toast({ tone: 'error', icon: 'AlertCircle', title: err?.message || 'Password update failed' });
      });
  };
  const setNotif = (key, v) => ss.update(s => ({ ...s, profile: { ...s.profile, notifications: { ...s.profile.notifications, [key]: v } } }));
  const setPref = (key, v) => setDraft(d => ({ ...d, [key]: v }));

  const notifItems = [
    { k: 'taskDue',      label: 'Task Due Reminders' },
    { k: 'dealStage',    label: 'Deal Stage Changes' },
    { k: 'mentions',     label: 'Mentions & Comments' },
    { k: 'weeklyDigest', label: 'Weekly Digest' },
    { k: 'productNews',  label: 'Product Updates' },
    { k: 'systemAlerts', label: 'System Notifications' },
  ];

  return (
    <div className="nx-settings-page nx-sg-page">
      <div className="nx-sg-header">
        <h2 className="nx-settings-page-title">General &amp; Profile</h2>
        <p className="nx-settings-page-sub">Manage your personal information, security and workspace preferences.</p>
      </div>

      {/* ── Card 1: Profile Information ── */}
      <div className="nx-sg-card">
        <div className="nx-sg-card-head">
          <span className="nx-sg-card-title">Profile Information</span>
        </div>
        <div className="nx-sg-card-body">
          <div className="nx-sg-profile-grid">
            {/* Left: Avatar */}
            <div className="nx-sg-avatar-col">
              <Avatar name={draft.name} tone={draft.avatarTone} size={64} />
              <div className="nx-sg-avatar-actions">
                <Btn variant="ghost" size="sm" icon="UploadCloud" onClick={() => toast({ tone: 'success', icon: 'UploadCloud', title: 'Photo Picker Opened' })}>Upload Photo</Btn>
                <Btn variant="ghost" size="sm" onClick={() => ss.update(s => ({ ...s, profile: { ...s.profile, avatarTone: '#' + Math.floor(Math.random()*0xffffff).toString(16).padStart(6,'0') } }))}>Remove Photo</Btn>
              </div>
            </div>
            {/* Right: Fields */}
            <div className="nx-sg-fields-col">
              <div className="nx-sg-field-grid">
                <div className="nx-form-row">
                  <label>Full Name</label>
                  <input className="nx-input" value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} />
                </div>
                <div className="nx-form-row">
                  <label>Email Address</label>
                  <input className="nx-input" type="email" value={draft.email} onChange={e => setDraft(d => ({ ...d, email: e.target.value }))} />
                </div>
                <div className="nx-form-row">
                  <label>Phone Number</label>
                  <input className="nx-input" value={draft.phone || ''} onChange={e => setDraft(d => ({ ...d, phone: e.target.value }))} />
                </div>
                <div className="nx-form-row">
                  <label>Role</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32 }}>
                    <Pill tone="purple">Admin</Pill>
                    <span className="nx-muted nx-mini">Managed by workspace admin</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="nx-sg-card-footer">
          <Btn variant="ghost" onClick={() => setDraft(profile)}>Cancel</Btn>
          <Btn variant="primary" icon="Check" onClick={save}>Save Changes</Btn>
        </div>
      </div>

      {/* ── Card 2: Security ── */}
      <div className="nx-sg-card">
        <div className="nx-sg-card-head">
          <span className="nx-sg-card-title">Security</span>
        </div>
        <div className="nx-sg-card-body">
          <div className="nx-sg-security-form">
            <div className="nx-form-row">
              <label>Current Password</label>
              <input className="nx-input" type="password" value={pwd.current} onChange={e => setPwd(p => ({ ...p, current: e.target.value }))} placeholder="Enter current password" />
            </div>
            <div className="nx-form-row">
              <label>New Password</label>
              <input className="nx-input" type="password" value={pwd.next} onChange={e => setPwd(p => ({ ...p, next: e.target.value }))} placeholder="At least 8 characters" />
            </div>
            <div className="nx-form-row">
              <label>Confirm New Password</label>
              <input className="nx-input" type="password" value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))} placeholder="Repeat new password" />
            </div>
          </div>
        </div>
        <div className="nx-sg-card-footer">
          <Btn variant="primary" icon="Shield" onClick={changePwd}>Update Password</Btn>
        </div>
      </div>

      {/* ── Card 3: Preferences ── */}
      <div className="nx-sg-card">
        <div className="nx-sg-card-head">
          <span className="nx-sg-card-title">Preferences</span>
        </div>
        <div className="nx-sg-card-body">
          {/* Appearance / regional prefs */}
          <div className="nx-sg-pref-grid">
            <div className="nx-form-row">
              <label>Theme</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32 }}>
                <Pill tone={ss.appearanceTone()}>{ss.appearanceLabel()}</Pill>
                <Btn variant="ghost" size="sm" icon="ArrowRight" onClick={() => window.dispatchEvent(new CustomEvent('nx-go-appearance'))}>Change</Btn>
              </div>
            </div>
            <div className="nx-form-row">
              <label>Language</label>
              <select className="nx-input" value={draft.language || 'en'} onChange={e => setPref('language', e.target.value)}>
                <option value="en">English (US)</option>
                <option value="en-gb">English (UK)</option>
                <option value="hi">Hindi</option>
              </select>
            </div>
            <div className="nx-form-row">
              <label>Time Zone</label>
              <select className="nx-input" value={draft.timezone || 'Asia/Kolkata'} onChange={e => setPref('timezone', e.target.value)}>
                <option value="Asia/Kolkata">India Standard Time (IST)</option>
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">London (GMT)</option>
              </select>
            </div>
            <div className="nx-form-row">
              <label>Date Format</label>
              <select className="nx-input" value={draft.dateFormat || 'DD/MM/YYYY'} onChange={e => setPref('dateFormat', e.target.value)}>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
          </div>

          {/* Notification Preferences */}
          <div className="nx-sg-notif-section">
            <div className="nx-sg-notif-label">Notification Preferences</div>
            <div className="nx-sg-notif-grid">
              {notifItems.map(n => (
                <div key={n.k} className="nx-sg-notif-item">
                  <span className="nx-sg-notif-text">{n.label}</span>
                  <Toggle on={!!(profile.notifications[n.k])} onChange={(v) => setNotif(n.k, v)} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="nx-sg-card-footer">
          <Btn variant="ghost" onClick={() => setDraft(profile)}>Cancel</Btn>
          <Btn variant="primary" icon="Check" onClick={save}>Save Changes</Btn>
        </div>
      </div>
    </div>
  );
};

/* =========================================================
   APPEARANCE — Light / Dark / System theme picker
   ========================================================= */
const SettingsAppearance = ({ ss }) => {
  const toast = useToast();
  const mode = ss.themeMode;
  const choose = (m) => {
    if (m === mode) return;
    ss.setTheme(m);
    const label = m === 'system' ? 'System' : m === 'dark' ? 'Dark' : 'Light';
    toast({ tone: 'success', icon: 'Check', title: label + ' Mode Enabled' });
  };
  const resolved = (window.NexusTheme && window.NexusTheme.getResolved()) || 'light';
  const options = [
    { id: 'light',  title: 'Light',  sub: 'Bright, default ' + (ss.state.whitelabel.brand || 'URSA') + ' look', previewClass: 'is-light' },
    { id: 'dark',   title: 'Dark',   sub: 'Easy on the eyes after hours',   previewClass: 'is-dark' },
    { id: 'system', title: 'System', sub: 'Follow your device preference', previewClass: 'is-system' },
  ];
  return (
    <div className="nx-settings-page">
      <h2 className="nx-settings-page-title">Appearance</h2>
      <p className="nx-settings-page-sub">Choose how {ss.state.whitelabel.brand || 'URSA'} looks. Your selection is saved to this device and applies across every page — sidebar, tables, drawers and dialogs.</p>

      <h3 className="nx-panel-title" style={{ marginTop: 8 }}>Theme</h3>
      <div className="nx-theme-grid">
        {options.map(opt => (
          <button
            key={opt.id}
            type="button"
            className={'nx-theme-card' + (mode === opt.id ? ' is-active' : '')}
            onClick={() => choose(opt.id)}
            aria-pressed={mode === opt.id}
          >
            <span className={'nx-theme-card-preview ' + opt.previewClass} />
            <span className="nx-theme-card-meta">
              <span className="nx-theme-card-title">{opt.title}</span>
              <span className="nx-theme-card-sub">{opt.sub}</span>
            </span>
            <span className="nx-theme-card-check"><NexusIcon name="Check" size={11} /></span>
          </button>
        ))}
      </div>
      <div className="nx-theme-hint">
        <NexusIcon name="Info" size={12} />
        <span>
          {mode === 'system'
            ? 'Currently showing ' + resolved + ' (matches your system).'
            : 'Saved on this device. Switches instantly with a smooth transition.'}
        </span>
      </div>
    </div>
  );
};

const Toggle = ({ on, onChange }) => (
  <button onClick={() => onChange(!on)} style={{
    width: 36, height: 20, borderRadius: 12, padding: 2, border: 'none',
    background: on ? 'var(--nx-accent, #125AB0)' : '#CBD3DD', cursor: 'pointer', transition: 'background 0.15s',
    display: 'inline-flex', alignItems: 'center', justifyContent: on ? 'flex-end' : 'flex-start',
  }}>
    <span style={{ width: 16, height: 16, borderRadius: 8, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }} />
  </button>
);

/* =========================================================
   MEMBERS — redirects to Workspace Admin (backend-wired)
   V1: member management uses workspace-admin/Membership.jsx
   which calls workspaceApi directly. Duplicate logic removed.
   ========================================================= */
const SettingsMembers = () => (
  <div className="nx-settings-page">
    <h2 className="nx-settings-page-title">Members</h2>
    <p className="nx-settings-page-sub">Manage workspace members, roles, and invitations.</p>
    <div className="nx-sg-card">
      <div className="nx-sg-card-head">
        <span className="nx-sg-card-title">Member Management</span>
      </div>
      <div className="nx-sg-card-body" style={{ padding: '24px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <span style={{ color: 'var(--nx-accent)', flexShrink: 0, marginTop: 2 }}>
            <NexusIcon name="Users" size={24} />
          </span>
          <div>
            <p style={{ margin: '0 0 8px', fontWeight: 600, color: 'var(--nx-text)' }}>
              Use Workspace Admin to manage members
            </p>
            <p style={{ margin: 0, color: 'var(--nx-text-muted)', fontSize: 13, lineHeight: 1.6 }}>
              Invite new members, change roles, and remove members from <strong>Workspace Admin &rarr; Members</strong>.
              Changes are saved immediately to the server.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MemberActions = ({ m, onEditRole, onDeactivate, onActivate, onDelete }) => {
  const [open, setOpen] = useStateS(false);
  const ref = React.useRef();
  useEffectS(() => { if (!open) return; const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }; document.addEventListener('mousedown', fn); return () => document.removeEventListener('mousedown', fn); }, [open]);
  return (
    <span ref={ref} style={{ position: 'relative' }}>
      <IconBtn icon="MoreVertical" size={14} onClick={() => setOpen(o => !o)} />
      {open && (
        <div className="nx-chip-menu" style={{ right: 0, left: 'auto' }}>
          <button className="nx-chip-menu-item" onClick={() => { onEditRole(); setOpen(false); }}><NexusIcon name="Edit" size={12} /><span>Change Role</span></button>
          {m.status === 'active' && <button className="nx-chip-menu-item" onClick={() => { onDeactivate(); setOpen(false); }}><NexusIcon name="UserX" size={12} /><span>Deactivate</span></button>}
          {m.status !== 'active' && <button className="nx-chip-menu-item" onClick={() => { onActivate(); setOpen(false); }}><NexusIcon name="UserCheck" size={12} /><span>Reactivate</span></button>}
          <button className="nx-chip-menu-item is-danger" onClick={() => { onDelete(); setOpen(false); }}><NexusIcon name="Trash2" size={12} /><span>Delete</span></button>
        </div>
      )}
    </span>
  );
};

const InviteMemberModal = ({ roles, onCancel, onInvite }) => {
  const [email, setEmail] = useStateS('');
  const [role, setRole] = useStateS(roles[2]?.id || roles[0].id);
  const submit = () => { if (!/.+@.+\..+/.test(email)) return; onInvite({ email, role }); };
  return (
    <Modal title="Invite Member" icon="UserPlus" onClose={onCancel} width={480}
      footer={<><Btn variant="ghost" onClick={onCancel}>Cancel</Btn><Btn variant="primary" icon="Mail" onClick={submit}>Send Invite</Btn></>}
    >
      <div className="nx-sfm-body">
        <p className="nx-sfm-desc">Send a secure invitation by email. The invitee will set their own password.</p>
        <div className="nx-form-row">
          <label>Email Address</label>
          <input className="nx-input" type="email" autoFocus value={email} onChange={e => setEmail(e.target.value)} placeholder="name@company.com" />
        </div>
        <div className="nx-form-row">
          <label>Assign Role</label>
          <select className="nx-input" value={role} onChange={e => setRole(e.target.value)}>
            {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
      </div>
    </Modal>
  );
};

const ReassignModal = ({ reassign, ss, ownedCounts, onConfirm, onClose }) => {
  const active = ss.state.members.filter(m => m.status === 'active' && m.id !== reassign.member.id);
  const [newOwner, setNewOwner] = useStateS(active[0]?.name || '');
  return (
    <Modal
      title={`Reassign Records Before ${reassign.mode === 'deactivate' ? 'Deactivating' : 'Deleting'}`}
      icon="AlertCircle"
      onClose={onClose}
      width={520}
      footer={<><Btn variant="ghost" onClick={onClose}>Cancel</Btn><Btn variant={reassign.mode === 'delete' ? 'danger' : 'primary'} icon="ArrowRight" onClick={() => onConfirm(newOwner)}>Reassign & {reassign.mode === 'deactivate' ? 'Deactivate' : 'Delete'}</Btn></>}
    >
      <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 12 }}><strong>{reassign.member.name}</strong> owns active records that need a new owner first.</p>
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', margin: '12px 0', flexWrap: 'wrap' }}>
        {Object.entries(ownedCounts).map(([k, v]) => v > 0 && (
          <span key={k} style={{ textAlign: 'center' }}><strong style={{ fontSize: 18, display: 'block' }}>{v}</strong><span className="nx-muted nx-mini">{MODULE_LABELS[k]}</span></span>
        ))}
      </div>
      <div className="nx-form-row"><label>Reassign To</label><select className="nx-input" value={newOwner} onChange={e => setNewOwner(e.target.value)}>{active.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}</select></div>
    </Modal>
  );
};

/* =========================================================
   ROLES & PERMISSIONS
   ========================================================= */
const SettingsRoles = ({ ss }) => {
  const store = useStore();
  const toast = useToast();
  const { roles } = ss.state;
  const [editing, setEditing] = useStateS(null);

  const save = (role) => {
    if (role.id) ss.update(s => ({ ...s, roles: s.roles.map(r => r.id === role.id ? role : r) }));
    else ss.update(s => ({ ...s, roles: [...s.roles, { ...role, id: 'r_' + Date.now(), members: 0, tone: 'blue' }] }));
    ss.addAudit({ who: ss.state.profile.name, action: role.id ? 'updated' : 'created', target: 'Role: ' + role.name, module: 'Roles' });
    toast({ tone: 'success', icon: 'Check', title: 'Role Saved' });
    setEditing(null);
  };
  const remove = (role) => {
    store.confirm({ title: 'Delete role "' + role.name + '"?', description: role.members > 0 ? 'This role is assigned to ' + role.members + ' members. They will be moved to Viewer.' : 'This action cannot be undone.', danger: true, onConfirm: () => {
      ss.update(s => ({ ...s, roles: s.roles.filter(r => r.id !== role.id), members: s.members.map(m => m.role === role.id ? { ...m, role: 'r_view' } : m) }));
      ss.addAudit({ who: ss.state.profile.name, action: 'deleted', target: 'Role: ' + role.name, module: 'Roles' });
      toast({ tone: 'success', icon: 'Trash', title: 'Role Deleted' });
    }});
  };

  return (
    <div className="nx-settings-page">
      <div className="nx-settings-page-head">
        <div>
          <h2 className="nx-settings-page-title">Roles & Permissions</h2>
          <p className="nx-settings-page-sub">{roles.length} roles · {roles.reduce((s, r) => s + r.members, 0)} assigned members. Configure team roles and permissions.</p>
        </div>
        <Btn variant="primary" icon="Plus" onClick={() => setEditing({ name: '', perms: ['view'], tone: 'blue' })}>New Role</Btn>
      </div>

      <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
        <thead><tr><Th icon="Shield">Role</Th><Th icon="Users">Members</Th><Th icon="Check">Permissions</Th><th className="nx-th-end" /></tr></thead>
        <tbody>
          {roles.map(r => (
            <tr key={r.id} className="nx-tr">
              <td><Pill tone={r.tone}>{r.name}</Pill></td>
              <td><span className="nx-cell is-mono">{r.members}</span></td>
              <td><span className="nx-tag-cell">{r.perms.map(p => <span key={p} className="nx-tag">{PERM_LABELS[p] || p}</span>)}</span></td>
              <td className="nx-td-end" style={{ whiteSpace: 'nowrap' }}>
                <Btn variant="ghost" size="sm" icon="Edit" onClick={() => setEditing(r)}>Edit</Btn>
                <IconBtn icon="Trash" size={13} onClick={() => remove(r)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table></div></div>

      {editing && <RoleEditor role={editing} onCancel={() => setEditing(null)} onSave={save} />}
    </div>
  );
};

const RoleEditor = ({ role, onCancel, onSave }) => {
  const [draft, setDraft] = useStateS(role);
  const togglePerm = (p) => setDraft(d => ({ ...d, perms: d.perms.includes(p) ? d.perms.filter(x => x !== p) : [...d.perms, p] }));
  const submit = () => { if (!draft.name.trim()) return; onSave({ ...draft }); };
  return (
    <Modal title={role.id ? 'Edit Role' : 'New Role'} icon="Shield" onClose={onCancel} width={580}
      footer={<><Btn variant="ghost" onClick={onCancel}>Cancel</Btn><Btn variant="primary" icon="Check" onClick={submit}>{role.id ? 'Save Changes' : 'Create Role'}</Btn></>}
    >
      <div className="nx-sfm-body">
        <div className="nx-form-row">
          <label>Role Name</label>
          <input className="nx-input" autoFocus value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="e.g. Account Executive" />
        </div>
        <div className="nx-form-row is-top">
          <label>Permissions</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {Object.entries(PERM_LABELS).map(([k, l]) => (
              <button key={k} className={'nx-chip' + (draft.perms.includes(k) ? ' is-active' : '')} onClick={() => togglePerm(k)}>
                <NexusIcon name={draft.perms.includes(k) ? 'Check' : 'Plus'} size={11} />{l}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

/* =========================================================
   CUSTOM FIELDS — backend-driven CRUD, archive, reorder
   Source of truth: orgCustomField table via /workspaces/:wsId/custom-fields/definitions
   ========================================================= */

/* Helpers for type and formula conversion */
const CF_TYPE_TO_BE  = { text: 'TEXT', number: 'NUMBER', select: 'SELECT', multi: 'MULTI_SELECT', date: 'DATE', checkbox: 'BOOLEAN' };
const CF_TYPE_FROM_BE = { TEXT: 'text', NUMBER: 'number', SELECT: 'select', MULTI_SELECT: 'multi', DATE: 'date', BOOLEAN: 'checkbox' };
const CF_PILL_TONE   = { text: 'gray', number: 'amber', select: 'blue', multi: 'purple', date: 'green', checkbox: 'gray' };
const CF_TYPE_LABEL  = { text: 'Text', number: 'Number', select: 'Select', multi: 'Multi-select', date: 'Date', checkbox: 'Boolean' };
const CF_MODULES     = Object.keys(MODULE_LABELS); // ['people','companies','deals','tasks']

const _cfMeta = (formula) => { try { return JSON.parse(formula || '{}'); } catch { return {}; } };
const _cfFormula = (existingFormula, searchable, filterable) => {
  const meta = _cfMeta(existingFormula);
  return JSON.stringify({ ...meta, searchable, filterable });
};
const _cfApiName = (label) => {
  const base = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return (base && /^[a-z]/.test(base)) ? base.slice(0, 40) : 'f_' + base.slice(0, 38);
};
/* Convert a backend orgCustomField to the UI shape expected by FieldEditor */
const _cfToUi = (f) => {
  const meta = _cfMeta(f.formula);
  return {
    id:         f.id,
    label:      f.label,
    type:       CF_TYPE_FROM_BE[f.type] || 'text',
    options:    f.options || [],
    required:   !!f.required,
    searchable: meta.searchable !== false,
    filterable: meta.filterable !== false,
    archived:   f.status === 'ARCHIVED',
    _raw:       f,
  };
};

const CfRowMenu = ({ f, open, onClose, onOpen, onEdit, onArchive, onRestore, onDelete }) => (
  <Menu open={open} onClose={onClose} trigger={
    <IconBtn icon="MoreHorizontal" size={16} title="Field actions" aria-label="Field actions" aria-haspopup="menu" onClick={onOpen} style={{ width: 36, height: 36, border: '1px solid var(--nx-border)' }} />
  }>
    <MenuItem icon="Edit2" onClick={onEdit}>Edit</MenuItem>
    {f.archived
      ? <MenuItem icon="RotateCcw" onClick={onRestore}>Restore</MenuItem>
      : <MenuItem icon="Archive" onClick={onArchive}>Archive</MenuItem>
    }
    <MenuDivider />
    <MenuItem icon="Trash2" danger onClick={onDelete}>Delete</MenuItem>
  </Menu>
);

const SettingsFields = ({ ss, wsId: wsIdProp }) => {
  /* wsIdProp is supplied by Workspace Admin; fall back to active CRM workspace */
  const wsId = wsIdProp || window.__nx_auth?.user?.activeWorkspaceId;
  const [activeModule, _setActiveModule] = useStateS(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      return (t && ['people','companies','deals','tasks'].includes(t)) ? t : 'people';
    } catch (_) { return 'people'; }
  });
  const setActiveModule = (m) => {
    _setActiveModule(m);
    try {
      const u = new URL(window.location.href);
      u.searchParams.set('tab', m);
      window.history.replaceState(null, '', u.pathname + u.search);
    } catch (_) {}
  };
  const [editing, setEditing]           = useStateS(null);
  const [allFields, setAllFields]       = useStateS({});
  const [loading, setLoading]           = useStateS(false);
  const [saving, setSaving]             = useStateS(false);
  const [search, setSearch]             = useStateS('');
  const [confirm, setConfirm]           = useStateS(null);
  const [openMenuId, setOpenMenuId]     = useStateS(null);
  const toast = useToast();

  /* Load all modules once on mount */
  useEffectS(() => {
    const api   = window.__nx_api?.customFields;
    if (!api || !wsId) {
      setAllFields(ss.state.customFields || {});
      return;
    }
    setLoading(true);
    Promise.all(CF_MODULES.map(m => api.getAllDefinitions(wsId, m).catch(() => [])))
      .then(results => {
        const map = {};
        CF_MODULES.forEach((m, i) => { map[m] = results[i] || []; });
        setAllFields(map);
      })
      .finally(() => setLoading(false));
  }, []);

  const rawFields = allFields[activeModule] || [];
  const uiFields  = rawFields.map(_cfToUi);
  const countActive = (m) => (allFields[m] || []).filter(f => f.status !== 'ARCHIVED').length;
  const filteredFields = search.trim()
    ? uiFields.filter(f => f.label.toLowerCase().includes(search.trim().toLowerCase()))
    : uiFields;

  /* --- CREATE or UPDATE -------------------------------------------------- */
  const save = async (uiField) => {
    const api  = window.__nx_api?.customFields;
    if (!api || !wsId) return;
    setSaving(true);
    try {
      const raw = rawFields.find(f => f.id === uiField.id);
      const formula = _cfFormula(raw?.formula ?? null, uiField.searchable !== false, uiField.filterable !== false);
      let saved;
      if (uiField.id) {
        saved = await api.updateDefinition(wsId, uiField.id, {
          label:    uiField.label,
          type:     CF_TYPE_TO_BE[uiField.type] || 'TEXT',
          required: !!uiField.required,
          options:  uiField.options || [],
          formula,
        });
        setAllFields(prev => ({ ...prev, [activeModule]: (prev[activeModule] || []).map(f => f.id === saved.id ? saved : f) }));
      } else {
        saved = await api.createDefinition(wsId, {
          label:   uiField.label,
          apiName: _cfApiName(uiField.label),
          entity:  activeModule,
          type:    CF_TYPE_TO_BE[uiField.type] || 'TEXT',
          required: !!uiField.required,
          options:  uiField.options || [],
          formula,
        });
        setAllFields(prev => ({ ...prev, [activeModule]: [...(prev[activeModule] || []), saved] }));
      }
      toast({ tone: 'success', icon: 'Check', title: 'Field Saved' });
      setEditing(null);
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: 'Save Failed', body: e.message });
    } finally {
      setSaving(false);
    }
  };

  /* --- ARCHIVE (soft-delete) ---------------------------------------------- */
  const remove = async (f) => {
    const api  = window.__nx_api?.customFields;
    if (!api || !wsId) return;
    try {
      const saved = await api.deleteDefinition(wsId, f._raw.id);
      setAllFields(prev => ({ ...prev, [activeModule]: (prev[activeModule] || []).map(x => x.id === saved.id ? saved : x) }));
      toast({ tone: 'success', icon: 'Archive', title: 'Field Archived' });
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: 'Archive Failed', body: e.message });
    }
  };

  /* --- TOGGLE required / archived / searchable / filterable -------------- */
  const toggle = async (f, key) => {
    const api  = window.__nx_api?.customFields;
    if (!api || !wsId) return;
    try {
      let dto = {};
      if (key === 'archived') {
        dto.status = f.archived ? 'ACTIVE' : 'ARCHIVED';
      } else if (key === 'required') {
        dto.required = !f.required;
      } else if (key === 'searchable' || key === 'filterable') {
        const meta = _cfMeta(f._raw.formula);
        meta[key] = !f[key];
        dto.formula = JSON.stringify(meta);
      }
      const saved = await api.updateDefinition(wsId, f._raw.id, dto);
      setAllFields(prev => ({ ...prev, [activeModule]: (prev[activeModule] || []).map(x => x.id === saved.id ? saved : x) }));
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: 'Update Failed', body: e.message });
    }
  };

  /* --- REORDER ------------------------------------------------------------ */
  const move = async (i, dir) => {
    const target = i + dir;
    if (target < 0 || target >= rawFields.length) return;
    const fieldA = rawFields[i];
    const fieldB = rawFields[target];
    /* Optimistic local swap */
    setAllFields(prev => {
      const list = [...(prev[activeModule] || [])];
      [list[i], list[target]] = [list[target], list[i]];
      return { ...prev, [activeModule]: list };
    });
    const api  = window.__nx_api?.customFields;
    if (!api || !wsId) return;
    try {
      await Promise.all([
        api.updateDefinition(wsId, fieldA.id, { sortOrder: fieldB.sortOrder }),
        api.updateDefinition(wsId, fieldB.id, { sortOrder: fieldA.sortOrder }),
      ]);
    } catch (_) {
      /* On error, reload from backend to restore consistent state */
      api.getAllDefinitions(wsId, activeModule)
        .then(list => setAllFields(prev => ({ ...prev, [activeModule]: list })))
        .catch(() => {});
    }
  };

  return (
    <div className="nx-settings-page">
      <div className="nx-settings-page-head">
        <div>
          <h2 className="nx-settings-page-title">Custom Fields</h2>
          <p className="nx-settings-page-sub">Configure custom fields to capture structured data across your CRM records.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {CF_MODULES.map(m => (
          <button key={m} className={'nx-chip' + (activeModule === m ? ' is-active' : '')} onClick={() => { setActiveModule(m); setSearch(''); setOpenMenuId(null); }}>
            <NexusIcon name={m === 'people' ? 'User' : m === 'companies' ? 'Building' : m === 'deals' ? 'Target' : 'CheckSquare'} size={11} />
            {MODULE_LABELS[m]} <strong>{countActive(m)}</strong>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <SearchField value={search} onChange={e => setSearch(e.target.value)} onClear={() => setSearch('')} placeholder={`Search ${MODULE_LABELS[activeModule]} fields…`} style={{ width: 300 }} />
        <div style={{ flex: 1 }} />
        <Btn variant="primary" icon="Plus" onClick={() => setEditing({ label: '', type: 'text', options: [], required: false, searchable: true, filterable: true })}>New Field</Btn>
      </div>

      <div className="nx-table-wrap" style={{ border: '1px solid var(--nx-border)', borderRadius: 'var(--nx-radius)' }}><div className="nx-table-scroll"><table className="nx-table">
        <thead><tr>
          <th style={{ width: 72, minWidth: 72, padding: '0 8px', textAlign: 'center', borderTopLeftRadius: 'calc(var(--nx-radius) - 1px)' }}><span className="nx-th-inner"><span>Order</span></span></th>
          <th style={{ minWidth: 280, padding: '0 12px' }}><span className="nx-th-inner"><NexusIcon name="Hash" size={11} /><span>Field Label</span></span></th>
          <th style={{ width: 144, minWidth: 140, padding: '0 12px' }}><span className="nx-th-inner"><NexusIcon name="Tag" size={11} /><span>Type</span></span></th>
          <th style={{ width: 110, minWidth: 100, textAlign: 'center', padding: '0 8px' }}><span className="nx-th-inner"><NexusIcon name="Check" size={11} /><span>Required</span></span></th>
          <th style={{ width: 120, minWidth: 110, textAlign: 'center', padding: '0 8px' }}><span className="nx-th-inner"><NexusIcon name="Search" size={11} /><span>Searchable</span></span></th>
          <th style={{ width: 120, minWidth: 110, textAlign: 'center', padding: '0 8px' }}><span className="nx-th-inner"><NexusIcon name="Filter" size={11} /><span>Filterable</span></span></th>
          <th style={{ width: 105, minWidth: 100, textAlign: 'center', padding: '0 8px' }}><span className="nx-th-inner"><NexusIcon name="Eye" size={11} /><span>Active</span></span></th>
          <th style={{ width: 88, minWidth: 80, textAlign: 'center', padding: '0 8px', borderTopRightRadius: 'calc(var(--nx-radius) - 1px)' }}><span className="nx-th-inner"><span>Actions</span></span></th>
        </tr></thead>
        <tbody>
          {loading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: '20px 0' }}><NexusIcon name="Loader" size={16} /><span className="nx-muted" style={{ marginLeft: 6 }}>Loading fields…</span></td></tr>}
          {!loading && uiFields.length === 0 && (
            <tr><td colSpan={8}><EmptyState icon="Hash" title={`No custom fields for ${MODULE_LABELS[activeModule]}`} desc="Add the first field using the New Field button." /></td></tr>
          )}
          {!loading && uiFields.length > 0 && filteredFields.length === 0 && (
            <tr><td colSpan={8}><EmptyState icon="Search" title="No fields match" desc={`No results for "${search}"`} /></td></tr>
          )}
          {!loading && filteredFields.map((f) => {
            const origIdx = rawFields.findIndex(r => r.id === f._raw.id);
            return (
              <tr key={f.id} className={'nx-tr' + (f.archived ? ' is-archived' : '')} style={f.archived && openMenuId !== f.id ? { opacity: 0.65 } : null}>
                <td style={{ textAlign: 'center', padding: '0 8px' }}><span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                  <IconBtn icon="ChevronUp" size={13} onClick={() => move(origIdx, -1)} title="Move up" disabled={!!search} />
                  <IconBtn icon="ChevronDown" size={13} onClick={() => move(origIdx, +1)} title="Move down" disabled={!!search} />
                </span></td>
                <td style={{ whiteSpace: 'normal', padding: '10px 12px' }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600, lineHeight: '1.3', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.label}</span>
                  {f._raw?.apiName && <span className="nx-muted" style={{ display: 'block', fontSize: 11, fontFamily: 'monospace', marginTop: 2, opacity: 0.75 }}>{f._raw.apiName}</span>}
                  {f.archived && <Pill tone="gray" size="xs" style={{ marginTop: 4 }}>Archived</Pill>}
                </td>
                <td><Pill tone={CF_PILL_TONE[f.type] || 'gray'}>{CF_TYPE_LABEL[f.type] || f.type}</Pill></td>
                <td style={{ textAlign: 'center', padding: '0 8px' }}><Toggle on={!!f.required}   onChange={() => toggle(f, 'required')} /></td>
                <td style={{ textAlign: 'center', padding: '0 8px' }}><Toggle on={!!f.searchable} onChange={() => toggle(f, 'searchable')} /></td>
                <td style={{ textAlign: 'center', padding: '0 8px' }}><Toggle on={!!f.filterable} onChange={() => toggle(f, 'filterable')} /></td>
                <td style={{ textAlign: 'center', padding: '0 8px' }}><Toggle on={!f.archived}    onChange={() => toggle(f, 'archived')} /></td>
                <td style={{ textAlign: 'center', padding: '0 8px' }}>
                  <CfRowMenu
                    f={f}
                    open={openMenuId === f.id}
                    onClose={() => setOpenMenuId(null)}
                    onOpen={() => setOpenMenuId(openMenuId === f.id ? null : f.id)}
                    onEdit={() => { setOpenMenuId(null); setEditing({ ...f }); }}
                    onArchive={() => { setOpenMenuId(null); setConfirm({ action: 'archive', field: f }); }}
                    onRestore={() => { setOpenMenuId(null); toggle(f, 'archived'); }}
                    onDelete={() => { setOpenMenuId(null); setConfirm({ action: 'delete', field: f }); }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table></div></div>

      {editing && <FieldEditor field={editing} onCancel={() => setEditing(null)} onSave={save} module={activeModule} saving={saving} />}

      {confirm?.action === 'archive' && (
        <ConfirmDialog
          title={confirm.field.archived ? 'Restore Field' : 'Archive Field'}
          desc={confirm.field.archived
            ? `Restore "${confirm.field.label}"? It will reappear on ${MODULE_LABELS[activeModule]} records and its values will be accessible again.`
            : `Archive "${confirm.field.label}"? It will be hidden from ${MODULE_LABELS[activeModule]} records. Existing values are preserved and the field can be restored at any time.`
          }
          confirmLabel={confirm.field.archived ? 'Restore' : 'Archive'}
          onConfirm={() => { toggle(confirm.field, 'archived'); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm?.action === 'delete' && (
        <ConfirmDialog
          title="Delete Field"
          desc={`Permanently delete "${confirm.field.label}"? All values stored for this field across ${MODULE_LABELS[activeModule]} records will be lost. This cannot be undone.`}
          danger
          confirmLabel="Delete"
          onConfirm={() => { remove(confirm.field); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

const FieldEditor = ({ field, onCancel, onSave, module: mod, saving }) => {
  const [draft, setDraft] = useStateS({ ...field, options: field.options || [] });
  const [optDraft, setOptDraft] = useStateS('');
  const addOpt = () => { if (!optDraft.trim()) return; setDraft(d => ({ ...d, options: [...d.options, optDraft.trim()] })); setOptDraft(''); };
  const submit = () => { if (!draft.label.trim() || saving) return; onSave({ ...draft }); };
  return (
    <Modal title={field.id ? 'Edit Field' : 'New Field'} icon="Hash" onClose={onCancel} width={480}
      footer={<><Btn variant="ghost" onClick={onCancel}>Cancel</Btn><Btn variant="primary" icon="Check" onClick={submit} disabled={saving}>{saving ? 'Saving…' : (field.id ? 'Save Changes' : 'Create Field')}</Btn></>}
    >
      {mod && <div className="nx-sfm-subtitle" style={{ marginBottom: 12 }}>{MODULE_LABELS[mod]}</div>}
      <div className="nx-sfm-body">
        <div className="nx-form-row">
          <label>Field Label</label>
          <input className="nx-input" autoFocus value={draft.label} onChange={e => setDraft(d => ({ ...d, label: e.target.value }))} placeholder="e.g. Contract Value" />
        </div>
        <div className="nx-form-row">
          <label>Type</label>
          <select className="nx-input" value={draft.type} onChange={e => setDraft(d => ({ ...d, type: e.target.value }))}>
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="select">Single-select</option>
            <option value="multi">Multi-select</option>
            <option value="date">Date</option>
            <option value="checkbox">Checkbox</option>
          </select>
        </div>
        {(draft.type === 'select' || draft.type === 'multi') && (
          <div className="nx-form-row is-top">
            <label>Options</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {draft.options.map((o, i) => (
                  <span key={i} className="nx-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {o}
                    <button style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }} onClick={() => setDraft(d => ({ ...d, options: d.options.filter((_, x) => x !== i) }))}>
                      <NexusIcon name="X" size={9} />
                    </button>
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="nx-input" value={optDraft} onChange={e => setOptDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && addOpt()} placeholder="Add option…" />
                <Btn variant="ghost" icon="Plus" onClick={addOpt}>Add</Btn>
              </div>
            </div>
          </div>
        )}
        <div className="nx-sfm-toggles">
          {[['required','Required'],['searchable','Searchable'],['filterable','Filterable']].map(([k, lbl]) => (
            <div key={k} className="nx-sfm-toggle-row">
              <span className="nx-sfm-toggle-label">{lbl}</span>
              <Toggle on={!!draft[k]} onChange={v => setDraft(d => ({ ...d, [k]: v }))} />
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

/* =========================================================
   WHITE LABEL — Live Branding Engine
   ========================================================= */
const WL_DEFAULTS = {
  brand: 'URSA', domain: '', domainStatus: 'UNVERIFIED',
  primaryColor: '#125AB0', secondaryColor: '#e8f0fe', accentColor: '#4692EC',
  successColor: '#0E8C5A', warningColor: '#B5740E', dangerColor: '#DE3217',
  logo: false, favicon: false, loginBackground: '', loginBanner: '',
  welcomeMessage: 'Welcome back', loginTagline: 'Powered by URSA',
  supportEmail: '', supportPhone: '', supportWebsite: '',
  footerText: '© 2026 URSA', privacyUrl: '', termsUrl: '', cookieUrl: '',
  applyScope: 'current',
};

const applyBrandingGlobally = (wl) => {
  applyBrandingTokens(wl);
  if (wl.favicon && wl.faviconUrl) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = wl.faviconUrl;
  }
  if (wl.brand) document.title = wl.brand;
};

const ColorRow = ({ label, value, onChange }) => (
  <div className="nx-wl-color-row">
    <input type="color" value={value} onChange={e => onChange(e.target.value)}
      className="nx-wl-color-swatch" style={{ background: value }} />
    <div className="nx-wl-color-meta">
      <span className="nx-wl-color-label">{label}</span>
      <span className="nx-wl-color-hex">{value}</span>
    </div>
    <div className="nx-wl-color-preview" style={{ background: value }} />
  </div>
);

const DomainStatus = ({ status }) => {
  const cfg = {
    verified:   { dot: '#0E8C5A', label: 'Verified',     bg: '#E7F5EE', fg: '#0E8C5A' },
    pending:    { dot: '#B5740E', label: 'Pending',       bg: '#FEF4E2', fg: '#A56212' },
    unverified: { dot: '#94A3B8', label: 'Not Verified',  bg: '#F1F4F8', fg: '#475569' },
  }[(status || '').toLowerCase()] || { dot: '#94A3B8', label: 'Unknown', bg: '#F1F4F8', fg: '#475569' };
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:999, background:cfg.bg, color:cfg.fg }}>
      <span style={{ width:7, height:7, borderRadius:'50%', background:cfg.dot, display:'inline-block' }} />
      {cfg.label}
    </span>
  );
};

const WLPreviewPanel = ({ form }) => {
  const pri = form.primaryColor || '#125AB0';
  const sec = form.secondaryColor || '#EDF4FD';
  const navItems = ['Home','People','Pipeline','Tasks'];
  return (
    <div className="nx-wl-preview">
      <div className="nx-wl-preview-head">Live Preview</div>

      {/* Sidebar mockup */}
      <div className="nx-wl-preview-section">
        <div className="nx-wl-preview-label">Sidebar</div>
        <div className="nx-wl-prev-sidebar">
          <div className="nx-wl-prev-brand" style={{ background: pri }}>
            <span style={{ width:18, height:18, borderRadius:4, background:'rgba(255,255,255,0.25)', display:'block' }} />
            <span style={{ fontSize:10, fontWeight:800, color:'#fff', letterSpacing:1 }}>{form.brand || 'URSA'}</span>
          </div>
          <div className="nx-wl-prev-nav">
            {navItems.map((item, i) => (
              <div key={item} className="nx-wl-prev-nav-item" style={i === 0 ? { background: pri + '18', color: pri } : {}}>
                <span className="nx-wl-prev-nav-dot" style={i === 0 ? { background: pri } : {}} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Button samples */}
      <div className="nx-wl-preview-section">
        <div className="nx-wl-preview-label">Buttons &amp; Accents</div>
        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
          <span className="nx-wl-prev-btn" style={{ background: pri, color:'#fff' }}>Primary</span>
          <span className="nx-wl-prev-btn" style={{ background: sec, color: pri, border: '1px solid ' + pri + '30' }}>Secondary</span>
          <span className="nx-wl-prev-btn" style={{ background:'transparent', color: pri, border:'1px solid ' + pri }}>Outline</span>
        </div>
      </div>

      {/* Color palette */}
      <div className="nx-wl-preview-section">
        <div className="nx-wl-preview-label">Color Palette</div>
        <div style={{ display:'flex', gap:4 }}>
          {[
            { c: form.primaryColor, l: 'Primary' },
            { c: form.accentColor, l: 'Accent' },
            { c: form.successColor, l: 'Success' },
            { c: form.warningColor, l: 'Warning' },
            { c: form.dangerColor, l: 'Danger' },
          ].map(({ c, l }) => (
            <div key={l} title={l} style={{ flex:1, height:28, borderRadius:6, background: c || '#ccc' }} />
          ))}
        </div>
      </div>

      {/* Login preview */}
      <div className="nx-wl-preview-section">
        <div className="nx-wl-preview-label">Login Screen</div>
        <div className="nx-wl-prev-login" style={{ background: form.loginBackground ? `url(${form.loginBackground}) center/cover` : pri + '12' }}>
          <div className="nx-wl-prev-login-card">
            <div style={{ width:24, height:24, borderRadius:6, background: pri, margin:'0 auto 6px' }} />
            <div style={{ fontSize:10, fontWeight:800, color:'var(--nx-text)', textAlign:'center', marginBottom:2 }}>
              {form.welcomeMessage || 'Welcome back'}
            </div>
            <div style={{ fontSize:9, color:'var(--nx-text-muted)', textAlign:'center' }}>
              {form.loginTagline || 'Powered by URSA'}
            </div>
          </div>
        </div>
      </div>

      {/* Favicon */}
      <div className="nx-wl-preview-section">
        <div className="nx-wl-preview-label">Favicon</div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:16, height:16, borderRadius:2, background: pri, flexShrink:0 }} />
          <span style={{ fontSize:11, color:'var(--nx-text-muted)' }}>{form.brand || 'URSA'} — Tab</span>
        </div>
      </div>
    </div>
  );
};

const SettingsWhiteLabel = ({ ss }) => {
  const toast = useToast();
  const store = useStore();
  const wl = ss.state.whitelabel || {};
  const [form, setForm] = useStateS({ ...WL_DEFAULTS, ...wl });
  const [resetOpen, setResetOpen] = useStateS(false);
  const [uploadingField, setUploadingField] = useStateS(null);
  const F = (patch) => setForm(f => ({ ...f, ...patch }));

  const handleAssetUpload = async (formField, file, flagField) => {
    if (!file || !file.type.startsWith('image/')) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Images only', desc: 'PNG, JPG, SVG, or WebP accepted.' });
      return;
    }
    const wsId = store.workspaceId;
    if (!wsId || !window.__nx_api?.branding?.uploadWhiteLabelAsset) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Upload unavailable' });
      return;
    }
    setUploadingField(formField);
    try {
      const { url } = await window.__nx_api.branding.uploadWhiteLabelAsset(wsId, file);
      F({ [formField]: url, ...(flagField ? { [flagField]: true } : {}) });
      toast({ tone: 'success', icon: 'UploadCloud', title: 'Uploaded', desc: 'Click Save White Label to persist.' });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Upload failed', desc: err?.message });
    } finally {
      setUploadingField(null);
    }
  };

  /* Load persisted white-label settings from backend on mount.
     Only populates the form — does NOT call applyBrandingGlobally.
     Branding is already correctly applied by loadWorkspace (resolved-branding
     API, full org/platform inheritance chain). Applying raw WL here would race
     with that resolved fetch and could overwrite the correct inherited values. */
  useEffectS(() => {
    const wsId = store.workspaceId;
    if (!wsId || !window.__nx_api?.branding) return;
    window.__nx_api.branding.getWhiteLabel(wsId)
      .then(data => {
        if (data) {
          const migrated = migrateWl(data);
          const loaded = { ...WL_DEFAULTS, ...migrated };
          setForm(loaded);
          ss.update(s => ({ ...s, whitelabel: migrated }));
        }
      })
      .catch(() => {}); /* fall back to localStorage values on error */
  }, []);

  const save = () => {
    const wsId = store.workspaceId;
    const fields = ['primaryColor','secondaryColor','accentColor','successColor','warningColor','dangerColor','brand','domain'];
    fields.forEach(k => {
      if ((wl[k] || WL_DEFAULTS[k]) !== form[k]) {
        ss.addAudit({ who: ss.state.profile.name, action: 'changed', target: `White Label: ${k} → ${form[k]}`, module: 'White Label' });
      }
    });
    ss.update(s => ({ ...s, whitelabel: { ...s.whitelabel, ...form } }));
    applyBrandingGlobally(form);
    if (wsId && window.__nx_api?.branding) {
      /* Strip DB-only fields not in UpsertWhiteLabelDto; forbidNonWhitelisted: true rejects them */
      const { id: _id, workspaceId: _wid, createdAt: _ca, updatedAt: _ua, domainStatus: _ds, ...payload } = form;
      window.__nx_api.branding.updateWhiteLabel(wsId, payload)
        .then(() => {
          window.dispatchEvent(new CustomEvent('nx-wl-saved', { detail: { wsId } }));
          toast({ tone: 'success', icon: 'Globe', title: 'White Label Saved', desc: form.applyScope === 'all' ? 'Applied to entire platform' : 'Applied to current workspace' });
        })
        .catch(err => toast({ tone: 'error', icon: 'AlertCircle', title: err?.message || 'Failed to save branding' }));
    } else {
      toast({ tone: 'success', icon: 'Globe', title: 'White Label Saved' });
    }
  };

  const resetToDefault = () => {
    const wsId = store.workspaceId;
    setForm({ ...WL_DEFAULTS });
    ss.update(s => ({ ...s, whitelabel: { ...WL_DEFAULTS } }));
    applyBrandingGlobally(WL_DEFAULTS);
    ss.addAudit({ who: ss.state.profile.name, action: 'reset branding', target: 'White Label: Reset to URSA defaults', module: 'White Label' });
    if (wsId && window.__nx_api?.branding) {
      window.__nx_api.branding.resetWhiteLabel(wsId)
        .then(() => {
          window.dispatchEvent(new CustomEvent('nx-wl-saved', { detail: { wsId } }));
          toast({ tone: 'success', icon: 'RefreshCw', title: 'Branding Reset to Defaults' });
        })
        .catch(err => toast({ tone: 'error', icon: 'AlertCircle', title: err?.message || 'Failed to reset branding' }));
    } else {
      toast({ tone: 'success', icon: 'RefreshCw', title: 'Branding Reset to Defaults' });
    }
    setResetOpen(false);
  };

  const FileUploadBtn = ({ label, formField, flagField }) => (
    <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
      <input type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) handleAssetUpload(formField, f, flagField); e.target.value = ''; }} />
      <Btn variant="ghost" size="sm" icon="UploadCloud" disabled={uploadingField === formField} as="span">
        {uploadingField === formField ? 'Uploading…' : label}
      </Btn>
    </label>
  );

  return (
    <div className="nx-settings-page nx-wl-page">
      <div className="nx-wl-layout">
        {/* ── LEFT: Form cards ── */}
        <div className="nx-wl-forms">
          {/* Header */}
          <div style={{ marginBottom: 20 }}>
            <h2 className="nx-settings-page-title">White Label</h2>
            <p className="nx-settings-page-sub">Customize platform branding. Changes apply instantly when saved.</p>
          </div>

          {/* Brand Assets */}
          <div className="nx-sg-card" style={{ marginBottom: 16 }}>
            <div className="nx-sg-card-head"><span className="nx-sg-card-title">Brand Assets</span></div>
            <div className="nx-sg-card-body">
              <div className="nx-wl-form-grid">
                <div className="nx-form-row">
                  <label>Brand Name</label>
                  <input className="nx-input" value={form.brand} onChange={e => F({ brand: e.target.value })} placeholder="URSA" />
                </div>
                <div className="nx-form-row">
                  <label>Logo</label>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <Toggle on={form.logo} onChange={v => F({ logo: v })} />
                      <FileUploadBtn label="Upload Logo" formField="faviconUrl" flagField="logo" />
                      {form.faviconUrl && form.logo && <Btn variant="ghost" size="sm" onClick={() => F({ logo: false, faviconUrl: '' })}>Remove</Btn>}
                    </div>
                    {form.logo && form.faviconUrl && (
                      <img src={form.faviconUrl} alt="Logo" style={{ height:36, maxWidth:140, objectFit:'contain', borderRadius:4, background:'#f5f5f5', padding:4, border:'1px solid var(--nx-border)' }} />
                    )}
                  </div>
                </div>
                <div className="nx-form-row">
                  <label>Favicon</label>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <Toggle on={form.favicon} onChange={v => F({ favicon: v })} />
                      <FileUploadBtn label="Upload Favicon" formField="faviconUrl" flagField="favicon" />
                      {form.faviconUrl && form.favicon && <Btn variant="ghost" size="sm" onClick={() => F({ favicon: false, faviconUrl: '' })}>Remove</Btn>}
                    </div>
                    {form.favicon && form.faviconUrl && (
                      <img src={form.faviconUrl} alt="Favicon" style={{ height:28, width:28, objectFit:'contain', borderRadius:4, background:'#f5f5f5', padding:2, border:'1px solid var(--nx-border)' }} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Colors */}
          <div className="nx-sg-card" style={{ marginBottom: 16 }}>
            <div className="nx-sg-card-head"><span className="nx-sg-card-title">Color Management</span></div>
            <div className="nx-sg-card-body">
              <div className="nx-wl-color-grid">
                <ColorRow label="Primary"   value={form.primaryColor}   onChange={v => F({ primaryColor: v })} />
                <ColorRow label="Secondary" value={form.secondaryColor} onChange={v => F({ secondaryColor: v })} />
                <ColorRow label="Accent"    value={form.accentColor || '#4692EC'} onChange={v => F({ accentColor: v })} />
                <ColorRow label="Success"   value={form.successColor || '#0E8C5A'} onChange={v => F({ successColor: v })} />
                <ColorRow label="Warning"   value={form.warningColor || '#B5740E'} onChange={v => F({ warningColor: v })} />
                <ColorRow label="Danger"    value={form.dangerColor || '#DE3217'} onChange={v => F({ dangerColor: v })} />
              </div>
            </div>
          </div>

          {/* Login Page */}
          <div className="nx-sg-card" style={{ marginBottom: 16 }}>
            <div className="nx-sg-card-head"><span className="nx-sg-card-title">Login Page</span></div>
            <div className="nx-sg-card-body">
              <div className="nx-wl-form-grid">
                <div className="nx-form-row">
                  <label>Welcome Message</label>
                  <input className="nx-input" value={form.welcomeMessage} onChange={e => F({ welcomeMessage: e.target.value })} placeholder="Welcome back" />
                </div>
                <div className="nx-form-row">
                  <label>Tagline</label>
                  <input className="nx-input" value={form.loginTagline} onChange={e => F({ loginTagline: e.target.value })} placeholder="Powered by URSA" />
                </div>
                <div className="nx-form-row">
                  <label>Background Image</label>
                  <div style={{ display:'flex', gap:6 }}>
                    <input className="nx-input" value={form.loginBackground} onChange={e => F({ loginBackground: e.target.value })} placeholder="URL or upload" style={{ flex:1 }} />
                    <FileUploadBtn label="Upload" formField="loginBackground" />
                  </div>
                </div>
                <div className="nx-form-row">
                  <label>Banner Image</label>
                  <div style={{ display:'flex', gap:6 }}>
                    <input className="nx-input" value={form.loginBanner} onChange={e => F({ loginBanner: e.target.value })} placeholder="URL or upload" style={{ flex:1 }} />
                    <FileUploadBtn label="Upload" formField="loginBanner" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Domain */}
          <div className="nx-sg-card" style={{ marginBottom: 16 }}>
            <div className="nx-sg-card-head">
              <span className="nx-sg-card-title">Custom Domain</span>
              {form.domain && <DomainStatus status={form.domainStatus || 'unverified'} />}
            </div>
            <div className="nx-sg-card-body">
              <div className="nx-wl-form-grid">
                <div className="nx-form-row">
                  <label>Domain</label>
                  <div style={{ display:'flex', gap:8 }}>
                    <input className="nx-input" value={form.domain} onChange={e => F({ domain: e.target.value })} placeholder="crm.yourcompany.com" style={{ flex:1 }} />
                    <Btn variant="ghost" size="sm" icon="Globe" onClick={() => { F({ domainStatus: 'pending' }); toast({ tone:'info', icon:'Globe', title:'Verification Initiated' }); }}>Verify</Btn>
                  </div>
                </div>
                <div className="nx-form-row">
                  <label>Verification Status</label>
                  <div style={{ height:32, display:'flex', alignItems:'center' }}>
                    <DomainStatus status={form.domainStatus || 'unverified'} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Support */}
          <div className="nx-sg-card" style={{ marginBottom: 16 }}>
            <div className="nx-sg-card-head"><span className="nx-sg-card-title">Support Details</span></div>
            <div className="nx-sg-card-body">
              <div className="nx-wl-form-grid">
                <div className="nx-form-row"><label>Support Email</label><input className="nx-input" value={form.supportEmail} onChange={e => F({ supportEmail: e.target.value })} placeholder="support@company.com" /></div>
                <div className="nx-form-row"><label>Support Phone</label><input className="nx-input" value={form.supportPhone} onChange={e => F({ supportPhone: e.target.value })} placeholder="+91-1800-XXX-XXXX" /></div>
                <div className="nx-form-row"><label>Support Website</label><input className="nx-input" value={form.supportWebsite || ''} onChange={e => F({ supportWebsite: e.target.value })} placeholder="https://company.com/support" /></div>
              </div>
            </div>
          </div>

          {/* Footer & Legal */}
          <div className="nx-sg-card" style={{ marginBottom: 16 }}>
            <div className="nx-sg-card-head"><span className="nx-sg-card-title">Footer &amp; Legal</span></div>
            <div className="nx-sg-card-body">
              <div className="nx-wl-form-grid">
                <div className="nx-form-row"><label>Footer Text</label><input className="nx-input" value={form.footerText} onChange={e => F({ footerText: e.target.value })} placeholder="© 2026 Your Company" /></div>
                <div className="nx-form-row"><label>Privacy Policy URL</label><input className="nx-input" value={form.privacyUrl} onChange={e => F({ privacyUrl: e.target.value })} placeholder="https://company.com/privacy" /></div>
                <div className="nx-form-row"><label>Terms &amp; Conditions URL</label><input className="nx-input" value={form.termsUrl} onChange={e => F({ termsUrl: e.target.value })} placeholder="https://company.com/terms" /></div>
                <div className="nx-form-row"><label>Cookie Policy URL</label><input className="nx-input" value={form.cookieUrl || ''} onChange={e => F({ cookieUrl: e.target.value })} placeholder="https://company.com/cookies" /></div>
              </div>
            </div>
          </div>

          {/* Actions footer */}
          <div className="nx-wl-actions">
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <Btn variant="ghost" icon="RotateCcw" onClick={() => setResetOpen(true)}>Reset to Default</Btn>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'var(--nx-text-muted)' }}>Apply to</label>
              <select className="nx-input" style={{ width:'auto' }} value={form.applyScope} onChange={e => F({ applyScope: e.target.value })}>
                <option value="current">Current Organization</option>
                <option value="all">Entire Platform</option>
              </select>
              <Btn variant="primary" icon="Globe" onClick={save}>Save White Label</Btn>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Live Preview ── */}
        <WLPreviewPanel form={form} />
      </div>

      {/* Reset confirmation modal */}
      {resetOpen && (
        <ConfirmDialog
          title="Reset Branding?"
          desc="This will restore all URSA default branding settings including colors, logo, domain, and footer text."
          danger={true}
          confirmLabel="Reset"
          onConfirm={resetToDefault}
          onCancel={() => setResetOpen(false)}
        />
      )}
    </div>
  );
};

/* =========================================================
   SUPPORT CENTER
   ========================================================= */
const SettingsSupportCenter = ({ ss }) => {
  const toast = useToast();
  const tickets = ss.state.supportTickets || [];
  const sTone = (s) => ({ open: 'amber', pending: 'blue', resolved: 'green' })[s] || 'gray';
  const resolve = (id) => {
    ss.update(s => ({ ...s, supportTickets: s.supportTickets.map(t => t.id === id ? { ...t, status: 'resolved' } : t) }));
    ss.addAudit({ who: ss.state.profile.name, action: 'resolved ticket', target: id, module: 'Support' });
    toast({ tone: 'success', icon: 'Check', title: 'Ticket Resolved' });
  };
  return (
    <div className="nx-settings-page">
      <div className="nx-settings-page-head">
        <div>
          <h2 className="nx-settings-page-title">Support Center</h2>
          <p className="nx-settings-page-sub">Workspace support tickets and requests.</p>
        </div>
        <Btn variant="primary" icon="Plus" onClick={() => toast({ tone: 'info', icon: 'MessageCircle', title: 'New support ticket' })}>New Ticket</Btn>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Open',     value: tickets.filter(t => t.status === 'open').length,     color: '#E08A00' },
          { label: 'Pending',  value: tickets.filter(t => t.status === 'pending').length,  color: '#125AB0' },
          { label: 'Resolved', value: tickets.filter(t => t.status === 'resolved').length, color: '#0E8C5A' },
        ].map(s => (
          <div key={s.label} style={{ flex: 1, background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
        <thead><tr>
          <th><span className="nx-th-inner"><NexusIcon name="Hash" size={12} /><span>Ticket</span></span></th>
          <th><span className="nx-th-inner"><NexusIcon name="Note" size={12} /><span>Subject</span></span></th>
          <th><span className="nx-th-inner"><NexusIcon name="AlertCircle" size={12} /><span>Priority</span></span></th>
          <th><span className="nx-th-inner"><NexusIcon name="User" size={12} /><span>Reporter</span></span></th>
          <th><span className="nx-th-inner"><NexusIcon name="Clock" size={12} /><span>Reported</span></span></th>
          <th><span className="nx-th-inner"><NexusIcon name="Activity" size={12} /><span>Status</span></span></th>
          <th className="nx-th-end" />
        </tr></thead>
        <tbody>{tickets.map(t => (
          <tr key={t.id} className="nx-tr">
            <td><span className="nx-cell is-mono">{t.id}</span></td>
            <td><span className="nx-row-name-text">{t.subject}</span></td>
            <td><Pill tone={t.priority === 'High' ? 'red' : t.priority === 'Medium' ? 'amber' : 'gray'}>{t.priority}</Pill></td>
            <td><span className="nx-muted">{t.reporter}</span></td>
            <td><span className="nx-muted">{t.time}</span></td>
            <td><Pill tone={sTone(t.status)}>{t.status}</Pill></td>
            <td className="nx-td-end">{t.status !== 'resolved' && <Btn variant="ghost" size="sm" icon="Check" onClick={() => resolve(t.id)}>Resolve</Btn>}</td>
          </tr>
        ))}</tbody>
      </table></div></div>
    </div>
  );
};

/* =========================================================
   AUDIT LOG
   ========================================================= */
const SettingsAudit = ({ ss }) => {
  const { audit } = ss.state;
  const [filterModule, setFilterModule] = useStateS(null);
  const [search, setSearch] = useStateS('');
  const filtered = useMemoS(() => audit.filter(a =>
    (!filterModule || a.module === filterModule) &&
    (!search || (a.who + a.action + a.target).toLowerCase().includes(search.toLowerCase()))
  ), [audit, filterModule, search]);
  const modules = ['CRM', 'Users', 'Licenses', 'White Label', 'Settings', 'Security', 'Support'];
  const moduleTone = (m) => ({ CRM: 'blue', Users: 'purple', Licenses: 'amber', 'White Label': 'green', Settings: 'gray', Security: 'red', Support: 'blue' })[m] || 'gray';

  return (
    <div className="nx-settings-page">
      <div className="nx-settings-page-head">
        <div>
          <h2 className="nx-settings-page-title">Audit Logs</h2>
          <p className="nx-settings-page-sub">{audit.length} events · organizations, users, licenses, white label, settings, CRM, and security.</p>
        </div>
        <Btn variant="ghost" icon="File" onClick={() => exportCSV('audit-log', audit)}>Export Log</Btn>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <SearchField
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search Audit Events…"
          aria-label="Search Audit Events"
          style={{ minWidth: 240 }}
        />
        <FilterChip icon="Tag" label="Category" value={filterModule} options={modules} onChange={setFilterModule} active={!!filterModule} />
        {filterModule && <button className="nx-chip is-clear" onClick={() => { setFilterModule(null); setSearch(''); }}><NexusIcon name="X" size={11} />Clear</button>}
      </div>
      <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
        <thead><tr><Th icon="User">Actor</Th><Th icon="Activity">Action</Th><Th icon="Tag">Target</Th><Th icon="Layers">Module</Th><Th icon="Clock">When</Th></tr></thead>
        <tbody>
          {filtered.length === 0 && <tr><td colSpan={5}><div className="nx-empty"><NexusIcon name="Activity" size={20} /><span>No audit events match your filters.</span></div></td></tr>}
          {filtered.map(a => (
            <tr key={a.id} className="nx-tr">
              <td><span className="nx-row-name"><Avatar name={a.who} tone="#125AB0" size={20} /><span className="nx-row-name-text">{a.who}</span></span></td>
              <td><Pill tone={a.action.includes('delet') ? 'red' : a.action.includes('creat') || a.action.includes('invit') ? 'green' : 'blue'}>{a.action}</Pill></td>
              <td>{a.target}</td>
              <td><Pill tone={moduleTone(a.module)}>{a.module}</Pill></td>
              <td><span className="nx-muted">{a.time}</span></td>
            </tr>
          ))}
        </tbody>
      </table></div></div>
    </div>
  );
};

window.SettingsView   = SettingsView;
window.SettingsFields = SettingsFields;
